import React, { createContext, useCallback, useContext, useMemo } from 'react';
import { observer } from 'mobx-react';
import $auth from 'app/stores/$auth';
import { getPortalVersion } from 'core/util/index';
import { LOGIN } from '@kentik/ui-shared/paths/login';
import { useHistory, useLocation } from 'react-router-dom';

export const LoginContext = createContext({});

const useSubmitLogin = () => {
  const location = useLocation();
  const history = useHistory();

  return useCallback(
    async (form, credentials) =>
      $auth.authenticate(credentials).then((result) => {
        if (result.success) {
          let pathname = $auth.isSynOnboarding ? '/v4/synthetics/tests/' : (location?.state?.from?.pathname ?? '/');
          if (pathname.startsWith('/login/')) {
            pathname = '/';
          }
          let search = location?.state?.from?.search ?? '';

          if (!$auth.isSharedUser && $auth.twoFactorVerifyRequired) {
            pathname = LOGIN.TWO_FACTOR;
            search = '';
          }

          // If the loaded version is out of date, let's just force a reload now
          getPortalVersion().then(({ hasOutdatedBundle }) => {
            if (hasOutdatedBundle) {
              window.location = `${pathname}${search}`;
            } else {
              // The query must go in `search`: history keeps a `?` inside `pathname`, so routes would not match.
              history.replace({ pathname, search, state: location.state });
            }
          });
        } else if (result.isAcl) {
          form.invalidate('Login was blocked due to a configured ACL.');
        } else {
          form.invalidate('Your email and password combination were incorrect.');
        }
      }),
    [history, location]
  );
};

/**
 * @param children {React.ReactNode}
 * @param subtenancy {object | undefined}
 * @param ssoCompanyIdentifier {string | undefined}
 * @returns {JSX.Element}
 * @constructor
 */
export const LoginContextProvider = observer(({ children, subtenancy, ssoCompanyIdentifier }) => {
  const handleSubmit = useSubmitLogin();
  const context = useMemo(
    () => ({ subtenancy, handleSubmit, ssoCompanyIdentifier }),
    [handleSubmit, ssoCompanyIdentifier, subtenancy]
  );
  return <LoginContext.Provider value={context}>{children}</LoginContext.Provider>;
});

/**
 * @returns {{subtenancy?: object, ssoCompanyIdentifier?: string, handleSubmit: (form: object, credentials: object) => void}}
 */
export const useLoginContext = () => useContext(LoginContext);
